import React from "react";
import { Link } from "react-router-dom";
import Seo, { buildFaqJsonLd } from "@/components/Seo";
import ParticleField from "@/components/motion/ParticleField";
import HexBackdrop from "@/components/motion/HexBackdrop";
import {
  ArrowRight,
  QrCode,
  Smartphone,
  Monitor,
  Database,
  Radio,
  Megaphone,
  Handshake,
  TrendingUp,
  Palette,
  LayoutGrid,
  Zap,
  Cpu,
  Wifi,
  BarChart3,
  Shield,
  Bell,
  Sparkles,
  MessageSquare,
  Users,
  Play,
  HelpCircle,
  ChevronDown,
  Search,
  Wrench,
  Rocket,
  LineChart,
  Coffee,
  Users2,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";
import { IMAGES } from "@/constants/site";

const MODULES = [
  { icon: QrCode, name: "Registration Platforms", body: "Journey-first badging, check-in, and access. Confirmation flows, waitlists, and dietary/access preferences captured up front.", details: ["QR + NFC badging", "Waitlist automation", "Group-block support"] },
  { icon: Smartphone, name: "Mobile App Integration", body: "Native or web, tied to your registration system or attendee community. Personalized schedules, session polls, and 1:1 networking built in.", details: ["Native iOS & Android", "Personalized agenda", "In-app networking"] },
  { icon: Monitor, name: "Onsite Tech PMs", body: "Certified technologists in the room — not just Zoom. Direct ownership of every device, cable, and check-in gate on show days.", details: ["Certified PMs on floor", "Device fleet ownership", "Runbook handoffs"] },
  { icon: Database, name: "Data Entry & Management", body: "Clean, structured, exportable data. Every registration, session touch, and lead capture streamed into your systems.", details: ["Salesforce / HubSpot", "Real-time exports", "Custom fields"] },
  { icon: Radio, name: "Virtual & Hybrid Meetings", body: "Broadcast-grade productions with global reach. Multi-camera studios, simulcast, and interactive audience layers.", details: ["Multi-cam studios", "Simulcast", "Sim-translation"] },
  { icon: Megaphone, name: "Attendee Outreach", body: "Segmented pre-, mid- and post-event communications. Cohort-based campaigns tied to registration data.", details: ["Segmented cohorts", "SMS + email", "Post-show nurture"] },
  { icon: Handshake, name: "Sponsorship Management", body: "Inventory, activation delivery, and revenue. Digital sponsor booths and measured lead ROI.", details: ["Sponsor inventory ops", "Lead capture", "Delivery audits"] },
  { icon: TrendingUp, name: "Business Strategy & Performance", body: "Program KPIs tied to revenue and growth. Post-show analytics turned into next-year briefs.", details: ["KPI framework", "Session-level analytics", "Executive briefs"] },
  { icon: Palette, name: "Marketing, Social & Branding", body: "Complete branding & marketing strategies with proven growth techniques.", details: ["Campaign strategy", "Identity systems", "Paid + organic"] },
  { icon: LayoutGrid, name: "Web Design & AI Custom Solutions", body: "Bespoke digital solutions and customized applications for the event: landing pages, portals, and integrations.", details: ["Landing + portal", "Session hubs", "Custom integrations", "AI Builds — AI Vibe coding for programs, features or products"] },
  { icon: Users2, name: "Business Match-Making Platform & Support", body: "Curated 1:1 buyer-supplier meetings with an AI-assisted matching engine, calendar orchestration, and on-site concierge support to make every introduction count.", details: ["AI-driven matching", "Calendar & meeting orchestration", "On-site concierge support", "ROI + follow-up reporting"] },
];

const USE_CASES = [
  { icon: Users, title: "Association Annual Meetings", body: "Member-first registration, sponsorship, and post-event analytics." },
  { icon: Bell, title: "Investigator Meetings", body: "Compliance-grade delivery for life-sciences programs." },
  { icon: Sparkles, title: "Product Launches", body: "Broadcast + physical + waitlist mechanics, unified." },
  { icon: MessageSquare, title: "Conferences & Sales Kickoffs", body: "Onsite check-in, session tracking, sponsor booths, and nurture." },
];

const STACK_LAYERS = [
  { icon: Cpu, label: "Runtime", body: "Show-day PMs, devices, cabling, and support ops." },
  { icon: Wifi, label: "Signal", body: "Registration, mobile, hybrid broadcast, and integrations." },
  { icon: BarChart3, label: "Intelligence", body: "Attendee data, KPIs, sponsor ROI, executive reporting." },
  { icon: Shield, label: "Governance", body: "Compliance, privacy, contracts, and audit-ready delivery." },
];

const EVENT_TECH_FAQS = [
  {
    q: "What exactly is 'event technology'?",
    a: "Event technology is the connected solution of software and hardware that runs your program end-to-end — attendee registration, event apps, badge printing, on-site check-in, digital signage, session polling, lead retrieval, and post-event analytics. Modern event tech unifies these tools into one dashboard so nothing lives in silos.",
  },
  {
    q: "What's the difference between event tech and AV?",
    a: "AV (audio-visual) is the physical production layer — sound, lighting, screens, staging. Event tech is the data and experience layer — how attendees register, arrive, engage in sessions, network, and how you measure it. Best programs treat them as complementary and integrate them under one show-caller.",
  },
  {
    q: "How do I choose the right registration platform?",
    a: "Match it to your program complexity. For under 300 attendees a simple form + payments works; for 500+ with tracks, VIPs, sessions, and reporting, you need workflows, invitation logic, and CRM sync. Also verify their SLA, GDPR/CCPA posture, and how badge / on-site check-in integrates — most 'gotchas' hide there.",
  },
  {
    q: "What is a hybrid event, and what tech do I actually need?",
    a: "A hybrid event serves both in-person and remote attendees simultaneously. Minimum solution: a streaming platform, an event app that mirrors both audiences, moderated Q&A, and one unified analytics view. Skip 'virtual venues' unless the audience really benefits — attention is scarcer online than in-room.",
  },
  {
    q: "How do I make sure the venue Wi-Fi can handle my attendees?",
    a: "Rule of thumb: 2–3 devices per attendee, ~2 Mbps down / 1 Mbps up per device for a smooth session. Ask the venue for their bandwidth commitment in writing, request dedicated event SSIDs, and — for anything over 300 pax — bring in a temp AP overlay from a specialist. We handle the site survey for you.",
  },
  {
    q: "What post-event metrics actually matter?",
    a: "Attendance rate vs. registered, session attendance heatmap, dwell time per booth/exhibitor, poll & Q&A engagement, NPS/CSAT, and pipeline influenced (if it's a marketing event). Vanity metrics like 'total impressions' rarely help — tie tech metrics back to your original event objective.",
  },
  {
    q: "How does on-site badge printing and check-in work?",
    a: "Attendees confirm at a kiosk or by scanning a QR from their confirmation email; the badge prints in under 3 seconds with role-based color coding and access track markers. Best-in-class systems reprint lost badges instantly, hard-block ghost registrations, and stream a live attendance count to your dashboard.",
  },
  {
    q: "What are 'integrations' and why do they matter?",
    a: "Integrations are the automated data bridges between your registration platform, CRM (Salesforce/HubSpot), event app, marketing automation, and finance systems. Without them, you spend weeks reconciling spreadsheets. With them, a new registration flows straight to sales and finance in real-time.",
  },
  {
    q: "How much should I budget for an event tech solution?",
    a: "Example: For a 500-person conference expect $18K–$45K for the tech layer alone (platform, app, on-site hardware, integrations, ops), varying by complexity and duration. Bigger flagships or multi-day executive programs run 2–5x. Investing here reliably reduces total event cost — fewer no-shows, faster check-in, better sponsor ROI.",
  },
  {
    q: "What happens if something breaks on show day?",
    a: "Every Magee Event Tech program ships with 24/7 show-day ops, a named tech lead in-room, hot spares for critical hardware (printers, scanners, APs), and a documented failover for every dependency. If something breaks, the attendee never sees it — that's the whole point.",
  },
];

export default function EventTech() {
  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="Event Tech Concierge — Registration, Mobile, Hybrid Broadcast, Data"
        description="Modular event technology engineered as one solution: registration platforms, native mobile apps, hybrid broadcast, real-time analytics, sponsor activations, and post-event data — for corporate meetings and associations."
        path="/services/event-tech"
        image={IMAGES.tech}
        keywords="event technology, event registration platform, event mobile app, hybrid event broadcast, event analytics, virtual event platform, sponsorship activation, event tech solution"
        jsonLd={buildFaqJsonLd(EVENT_TECH_FAQS)}
      />
      {/* HERO */}
      <section className="relative pt-28 md:pt-32 px-4 md:px-6">
        <div className="relative max-w-[1600px] mx-auto rounded-[28px] md:rounded-[36px] overflow-hidden min-h-[560px] md:min-h-[640px]">
          <img src={IMAGES.tech} alt="Event technology" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050A14]/50 via-[#050A14]/40 to-[#050A14]/90" />
          <div className="absolute inset-0 grid-pattern-dark opacity-30" />
          <div className="absolute -top-32 -left-24 w-[500px] h-[500px] rounded-full bg-cyan-400/25 blur-3xl" />
          <div className="absolute -bottom-32 -right-24 w-[600px] h-[600px] rounded-full bg-sky-500/25 blur-3xl" />

          <div className="relative px-4 md:px-14 pt-16 md:pt-24 pb-24 md:pb-28">
            <div className="flex items-center justify-center">
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                <Zap size={12} /> Event Tech Concierge · Modular Solution
              </span>
            </div>
            <h1 className="hero-headline text-white text-center mt-8 md:mt-10 leading-[0.95] text-[46px] sm:text-[60px] md:text-[76px] lg:text-[92px] max-w-5xl mx-auto text-balance px-2">
              Event Tech Concierge —{" "}
              <span className="hero-accent">Engineered as one solution.</span>
            </h1>
            <div className="flex justify-center mt-8">
              <span className="pulse-bar w-32 md:w-48" />
            </div>
            <p className="text-center text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mt-8">
              Ten modular products, one architecture. Registration, mobile, hybrid,
              data, and sponsorship — built so attendees feel one continuous
              experience and your team gets one accountable partner.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact" className="btn-primary">Request a Quote <ArrowRight size={16} /></Link>
              <a href="#modules" className="btn-dark backdrop-blur">See the Modules</a>
            </div>

            {/* Floating stat cards */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-5xl mx-auto">
              {[
                { k: "11", v: "Modules" },
                { k: "1", v: "Solution" },
                { k: "0", v: "Blind Spots" },
                { k: "24/7", v: "Show-Day Ops" },
              ].map((s) => (
                <div key={s.v} className="bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 text-white">
                  <div className="font-editorial text-3xl md:text-4xl tracking-tight">{s.k}</div>
                  <div className="text-white/60 text-xs md:text-sm mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MODULES — DARK GRID */}
      <section id="modules" className="relative py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-6 md:px-14 py-16 md:py-24">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <ParticleField count={22} className="opacity-70" />
            <div className="absolute -top-24 -left-20 w-[440px] h-[440px] rounded-full bg-cyan-500/15 blur-3xl" />
            <div className="absolute -bottom-24 -right-16 w-[520px] h-[520px] rounded-full bg-sky-500/15 blur-3xl" />
            <HexMark size={260} opacity={0.06} className="absolute right-8 top-16 rotate-[16deg] pointer-events-none hidden lg:block" />

            {/* 3D orbs — warm + cool */}

            <div className="relative">
              <Reveal>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                  <div>
                    <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25"><Zap size={12} /> The Eleven Service Modules</span>
                    <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] text-white max-w-3xl">
                      Every module —{" "}
                      <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                        independently sharp, jointly seamless.
                      </span>
                    </h2>
                  </div>
                  <p className="max-w-sm text-white/60">
                    Pick one, pick eleven. Each module is production-grade on its own —
                    but shows its real power when the solution runs as one.
                  </p>
                </div>
              </Reveal>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
                {MODULES.map((m, i) => (
                  <Reveal key={m.name} delay={i * 0.04}>
                    <div className="group relative bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-cyan-400/40 rounded-2xl p-6 transition-all duration-500 h-full">
                      <div className="icon-tile mb-5"><m.icon size={20} /></div>
                      <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-300/70">
                        /{String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="font-editorial text-lg leading-tight text-white mt-1">{m.name}</h3>
                      <p className="mt-3 text-white/60 text-[13px] leading-relaxed">{m.body}</p>
                      <ul className="mt-4 space-y-1.5 border-t border-white/10 pt-4">
                        {m.details.map((d) => (
                          <li key={d} className="flex items-start gap-2 text-[12px] text-white/70">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-cyan-400 shrink-0" />
                            {d}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEET YOUR TECH LEAD — 3D cutout section (moved to right after Services) */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-[#050A14]">
        {/* Ambient background */}
        <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
        <div className="pointer-events-none absolute top-[10%] left-[15%] w-[520px] h-[520px] rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-[5%] right-[10%] w-[480px] h-[480px] rounded-full bg-sky-500/15 blur-3xl" />

        {/* Perspective stage */}
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
            {/* LEFT — Copy */}
            <Reveal className="col-span-12 lg:col-span-6 order-2 lg:order-1">
              <span className="chip !bg-cyan-500/10 !text-cyan-300 !border-cyan-300/25">
                <Sparkles size={12} /> Your accountable tech lead
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.03em] text-white text-balance">
                One partner, one plan —{" "}
                <span className="italic font-medium bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                  every module accountable.
                </span>
              </h2>
              <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
                From registration to on-site communications to post-event analytics — a
                senior Magee tech lead owns your solution end-to-end. Real-time
                dashboards, proactive alerts, and a hand you can shake at 2am on
                show day.
              </p>
            </Reveal>

            {/* RIGHT — 3D cutout stage */}
            <Reveal delay={0.1} className="col-span-12 lg:col-span-6 order-1 lg:order-2">
              <div
                className="relative mx-auto w-full max-w-[560px] aspect-[3/4]"
                style={{ perspective: "1600px" }}
              >
                {/* Halo / spotlight behind subject */}
                <div className="absolute inset-x-[8%] top-[10%] bottom-[8%] rounded-[42%] bg-gradient-to-b from-cyan-400/40 via-sky-500/25 to-transparent blur-3xl" />

                {/* Depth backdrop panel — tilted */}
                <div
                  className="absolute inset-[6%] rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm shadow-[0_40px_100px_-30px_rgba(6,182,212,0.35)]"
                  style={{ transform: "rotateY(-8deg) rotateX(4deg) translateZ(-60px)" }}
                />

                {/* Cutout image with radial mask */}
                <div
                  className="absolute inset-0 flex items-end justify-center"
                  style={{ transform: "translateZ(60px)" }}
                >
                  <img
                    src="/event-tech/tech-leads-duo.jpg"
                    alt="Magee tech leads standing ready for show day"
                    className="w-full h-full object-cover object-top"
                    style={{
                      WebkitMaskImage:
                        "radial-gradient(ellipse 70% 85% at 50% 50%, #000 62%, rgba(0,0,0,0.7) 78%, rgba(0,0,0,0.12) 92%, transparent 100%)",
                      maskImage:
                        "radial-gradient(ellipse 70% 85% at 50% 50%, #000 62%, rgba(0,0,0,0.7) 78%, rgba(0,0,0,0.12) 92%, transparent 100%)",
                      mixBlendMode: "luminosity",
                      filter: "drop-shadow(0 30px 40px rgba(6,182,212,0.4)) drop-shadow(0 15px 25px rgba(0,0,0,0.6))",
                    }}
                  />
                  <img
                    src="/event-tech/tech-leads-duo.jpg"
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    style={{
                      WebkitMaskImage:
                        "radial-gradient(ellipse 60% 78% at 50% 50%, #000 45%, rgba(0,0,0,0.6) 68%, transparent 88%)",
                      maskImage:
                        "radial-gradient(ellipse 60% 78% at 50% 50%, #000 45%, rgba(0,0,0,0.6) 68%, transparent 88%)",
                    }}
                  />
                </div>

                {/* Floating tech cards (3D) */}
                <div
                  className="absolute -top-4 -left-8 md:-left-24 bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-2xl px-4 py-3 text-white shadow-[0_20px_50px_-15px_rgba(6,182,212,0.5)]"
                  style={{ transform: "rotateY(12deg) rotateX(-4deg) translateZ(120px)" }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-emerald-300">Live · Registrations</span>
                  </div>
                  <div className="font-editorial text-2xl leading-none">1,842</div>
                  <div className="text-white/50 text-[10px] mt-1">+128 in last hour</div>
                </div>

                <div
                  className="absolute top-[52%] -right-6 md:-right-24 bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-2xl px-4 py-3 text-white shadow-[0_20px_50px_-15px_rgba(6,182,212,0.5)]"
                  style={{ transform: "rotateY(-14deg) rotateX(6deg) translateZ(140px)" }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Wifi size={13} className="text-cyan-300" />
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-300">Onsite Wi-Fi</span>
                  </div>
                  <div className="font-editorial text-xl leading-none">Nominal</div>
                  <div className="text-white/50 text-[10px] mt-1">4 APs · 96% uptime</div>
                </div>

                <div
                  className="absolute -bottom-6 -left-6 md:-left-20 bg-white/[0.08] backdrop-blur-xl border border-white/15 rounded-2xl px-4 py-3 text-white shadow-[0_20px_50px_-15px_rgba(6,182,212,0.5)]"
                  style={{ transform: "rotateY(10deg) rotateX(-2deg) translateZ(150px)" }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 size={13} className="text-sky-300" />
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-sky-300">Session Poll</span>
                  </div>
                  <div className="font-editorial text-xl leading-none">92% CSAT</div>
                  <div className="text-white/50 text-[10px] mt-1">Track 2 · Keynote</div>
                </div>

                {/* Base reflection */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[60%] h-6 rounded-[50%] bg-cyan-400/40 blur-2xl" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <span className="chip"><span className="chip-dot" /> Use Cases</span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl">
                  Built for the programs where{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">every detail counts.</span>
                </h2>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {USE_CASES.map((u, i) => (
              <Reveal key={u.title} delay={i * 0.06}>
                <div className="card-white p-6 md:p-7 h-full">
                  <div className="icon-tile mb-5"><u.icon size={18} /></div>
                  <h3 className="font-editorial text-xl leading-tight">{u.title}</h3>
                  <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed">{u.body}</p>
                </div>
              </Reveal>
            ))}

            {/* & MORE — catch-all */}
            <Reveal delay={USE_CASES.length * 0.06}>
              <Link
                to="/contact"
                data-testid="use-cases-more-card"
                className="relative group card-white p-6 md:p-7 h-full flex flex-col justify-between overflow-hidden bg-gradient-to-br from-cyan-500/10 via-white to-sky-500/10 border-cyan-300/40 hover:border-cyan-400 transition-colors"
              >
                <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-cyan-400/20 blur-3xl" />
                <div>
                  <div className="icon-tile mb-5 !bg-gradient-to-br !from-cyan-500 !to-sky-600 !text-white !border-transparent">
                    <Sparkles size={18} />
                  </div>
                  <h3 className="font-editorial text-xl leading-tight">&amp; More Services</h3>
                  <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed">
                    We can support all types of events or meetings —
                    tell us what you&rsquo;re running and we&rsquo;ll shape a plan.
                  </p>
                </div>
                <span className="mt-6 inline-flex items-center gap-1 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] group-hover:gap-2 transition-all">
                  Start a conversation <ArrowRight size={12} />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — clickable, industry-tied, more human */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden" aria-labelledby="how-it-works-heading">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <HexBackdrop variant="sparse" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] -top-20 -left-20 opacity-25" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -bottom-24 -right-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Header */}
          <Reveal>
            <div className="grid grid-cols-12 gap-8 md:gap-12 items-end mb-10 md:mb-14">
              <div className="col-span-12 lg:col-span-8">
                <span className="chip">
                  <span className="chip-dot" /> How we actually do it
                </span>
                <h2
                  id="how-it-works-heading"
                  className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance"
                >
                  From{" "}
                  <span className="italic font-medium">discovery</span> to{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    show day
                  </span>{" "}
                  — and the debrief you actually want to read.
                </h2>
              </div>
              <div className="col-span-12 lg:col-span-4 lg:pl-4">
                <p className="text-[var(--text-muted)] leading-relaxed">
                  Four honest phases. Click any card below to see how we run it —
                  including the specific plays we call for corporate meetings,
                  associations, and industry conferences.{" "}
                  <span className="font-mono-brand text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]">
                    ↓ tap the cards
                  </span>
                </p>
              </div>
            </div>
          </Reveal>

          {/* Timeline connector line (desktop) */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-300/50 to-transparent"
            />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                {
                  n: "01",
                  icon: Search,
                  t: "Discover",
                  vibe: "The 'listen first, sell later' phase.",
                  d: "We start with questions, not a sales pitch. Goals, audiences, KPIs, and the realities nobody wants to say out loud.",
                  how: [
                    "Explore event objectives and requirements",
                    "Working session with your event owner + stakeholders",
                    "Audience, KPI, and budget alignment",
                    "A one-page canvas your exec team actually reads",
                  ],
                },
                {
                  n: "02",
                  icon: Wrench,
                  t: "Configure",
                  vibe: "The 'chef's kitchen' phase.",
                  d: "This is where the solution gets stood up — quietly, methodically, and always in a sandbox before it touches your attendees.",
                  how: [
                    "Project & logistics planning",
                    "Workflow, brand, and content tuned to your audience",
                    "Integrations wired to your existing systems",
                    "Sandbox rehearsal to catch the ugly edge cases early",
                  ],
                },
                {
                  n: "03",
                  icon: Rocket,
                  t: "Launch",
                  vibe: "The 'lights, camera, action' phase.",
                  d: "Show day. Doors open. Nothing breaks — because we already broke it in rehearsal. We stay from load-in to strike.",
                  how: [
                    "Named onsite tech lead + trained showrunners in-room",
                    "24/7 communications bridge with your team",
                    "Redundancy on every mission-critical layer",
                  ],
                },
                {
                  n: "04",
                  icon: LineChart,
                  t: "Optimize",
                  vibe: "The 'you're already planning next year' phase.",
                  d: "Post-event isn't a report — it's a decision. We hand you the details that shapes the next event or program.",
                  how: [
                    "Executive dashboard delivered within days, not weeks",
                    "Sponsor, session, and attendee signal you can act on",
                    "Debrief workshop: what to keep, kill, or dial up",
                  ],
                },
              ].map((s, i) => {
                const StepIcon = s.icon;
                return (
                  <Reveal key={s.n} delay={i * 0.06}>
                    <details
                      className="group relative card-white p-6 md:p-7 h-full rounded-2xl border border-[var(--border)] hover:border-cyan-300/60 open:border-cyan-400/70 open:shadow-[0_20px_50px_-20px_rgba(6,182,212,0.35)] transition-all duration-300 cursor-pointer"
                      data-testid={`how-it-works-step-${s.n}`}
                    >
                      <summary className="list-none marker:hidden select-none">
                        <div className="numeral-mono text-5xl md:text-7xl absolute -top-2 right-4 select-none group-open:text-cyan-500/30 transition-colors">
                          {s.n}
                        </div>

                        <div className="flex items-start justify-between gap-3">
                          <div className="icon-tile"><StepIcon size={18} /></div>
                          <ChevronDown
                            size={18}
                            className="mt-2 text-[var(--text-muted)] group-open:rotate-180 group-open:text-[var(--accent)] transition-transform duration-300"
                          />
                        </div>

                        <h3 className="mt-5 font-editorial text-2xl leading-tight">{s.t}</h3>
                        <p className="mt-1 text-[13px] italic text-[var(--accent)]">{s.vibe}</p>

                        <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed">{s.d}</p>

                        <span className="mt-4 inline-flex items-center gap-1 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] group-open:hidden">
                          Peek behind the curtain <ChevronDown size={11} />
                        </span>
                      </summary>

                      <div className="mt-5 pt-5 border-t border-[var(--border)]">
                        <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text)] mb-3">
                          What happens
                        </p>
                        <ul className="space-y-2.5">
                          {s.how.map((line) => (
                            <li key={line} className="flex items-start gap-2.5 text-[13.5px] leading-relaxed text-[var(--text)]/85">
                              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600" />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                        <p className="mt-4 text-[12.5px] italic text-[var(--text-muted)]">
                          The recipe stays in the kitchen — we&rsquo;ll walk you
                          through it live.
                        </p>
                      </div>
                    </details>
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* Casual closing note */}
          <Reveal delay={0.15}>
            <div className="mt-12 md:mt-16 rounded-2xl border border-dashed border-cyan-300/50 bg-white/60 backdrop-blur px-6 py-6 md:px-8 md:py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div className="flex items-start gap-3">
                <span className="shrink-0 w-10 h-10 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center">
                  <Coffee size={16} className="text-[var(--accent)]" />
                </span>
                <div>
                  <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--accent)]">
                    P.S.
                  </p>
                  <p className="font-editorial text-lg md:text-xl mt-1 leading-snug">
                    Coffee&rsquo;s on us. Bring your messiest event and we&rsquo;ll
                    map a plan on a napkin.
                  </p>
                </div>
              </div>
              <Link
                to="/contact#book"
                className="btn-primary shrink-0 w-full md:w-auto justify-center"
                data-testid="how-it-works-final-cta"
              >
                Grab 15 minutes <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EVENT TECH FAQS — educational, w/ inline CTAs */}
      <section className="relative py-20 md:py-28 bg-white overflow-hidden" aria-labelledby="event-tech-faqs-heading">
        <div className="absolute inset-0 hex-pattern opacity-30 pointer-events-none" />
        <HexBackdrop variant="dense" />
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-40 -right-24 opacity-25" />
        <div className="brand-bloom brand-bloom-blue w-[480px] h-[480px] -bottom-40 -left-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Section header */}
          <Reveal>
            <div className="grid grid-cols-12 gap-8 md:gap-12 items-end mb-12 md:mb-16">
              <div className="col-span-12 lg:col-span-8">
                <span className="chip">
                  <HelpCircle size={12} /> Event Technology FAQs
                </span>
                <h2 id="event-tech-faqs-heading" className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl text-balance">
                  10 questions{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    every event owner asks —
                  </span>{" "}
                  answered plainly.
                </h2>
              </div>
              <div className="col-span-12 lg:col-span-4 lg:pl-6">
                <p className="text-[var(--text-muted)] leading-relaxed">
                  From platform selection to show-day Wi-Fi, here&rsquo;s the
                  event tech field guide we wish every planner had before
                  their first RFP. Still stuck?{" "}
                  <Link
                    to="/contact"
                    className="text-[var(--accent)] hover:underline font-medium"
                    data-testid="faq-inline-help-link"
                  >
                    Our team is one click away.
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>

          {/* FAQ list */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {EVENT_TECH_FAQS.map((f, i) => (
              <React.Fragment key={f.q}>
                <Reveal delay={(i % 2) * 0.05}>
                  <details className="group card-white p-5 md:p-6 rounded-2xl border border-[var(--border)] hover:border-cyan-300/60 transition-colors duration-300">
                    <summary
                      className="flex items-start justify-between gap-4 cursor-pointer list-none marker:hidden select-none"
                      data-testid={`faq-question-${i + 1}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 text-white font-mono-brand text-[11px] flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(6,182,212,0.55)]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-editorial text-lg md:text-xl leading-snug tracking-[-0.01em] text-[var(--text)] pt-1">
                          {f.q}
                        </span>
                      </div>
                      <ChevronDown
                        size={20}
                        className="shrink-0 mt-2 text-[var(--text-muted)] group-open:rotate-180 group-open:text-[var(--accent)] transition-transform duration-300"
                      />
                    </summary>
                    <div className="mt-4 ml-11 text-[var(--text-muted)] leading-relaxed text-[15px]">
                      {f.a}
                    </div>
                  </details>
                </Reveal>

                {/* Sprinkled inline CTA every 3 questions on desktop */}
                {(i === 2 || i === 5) && (
                  <Reveal className="md:col-span-2" delay={0.05}>
                    <div className="my-2 rounded-2xl bg-gradient-to-br from-cyan-500/8 via-white to-sky-500/8 border border-cyan-300/25 p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <span className="shrink-0 w-9 h-9 rounded-full bg-white border border-cyan-300/40 flex items-center justify-center">
                          <MessageSquare size={16} className="text-[var(--accent)]" />
                        </span>
                        <div>
                          <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--accent)]">
                            Ask a Magee tech lead
                          </p>
                          <p className="font-editorial text-base md:text-lg text-[var(--text)] mt-1 leading-snug">
                            {i === 2
                              ? "Not sure which platform fits your program? Get a 15-min scope call."
                              : "Want a live show-day audit for your next event? We'll walk you through it."}
                          </p>
                        </div>
                      </div>
                      <Link
                        to="/contact"
                        className="btn-primary shrink-0 w-full sm:w-auto justify-center"
                        data-testid={i === 2 ? "faq-cta-scope" : "faq-cta-audit"}
                      >
                        {i === 2 ? "Book a scope call" : "Request an audit"} <ArrowRight size={14} />
                      </Link>
                    </div>
                  </Reveal>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Closing sprinkled CTA — after all 10 FAQs */}
          <Reveal delay={0.1}>
            <div className="mt-10 md:mt-14 text-center">
              <p className="text-[var(--text-muted)] max-w-2xl mx-auto leading-relaxed">
                Have a question we didn&rsquo;t answer? Our senior tech leads
                will scope, spec, and price a solution for your next event —
                usually within one business day.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/contact"
                  className="btn-primary"
                  data-testid="faq-cta-final-quote"
                >
                  Talk to our tech team <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact#book"
                  className="btn-dark"
                  data-testid="faq-cta-final-book"
                >
                  Book a 15-min call
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MODULE FILM — Enjoy product demos (moved after FAQs) */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-24 -left-16 opacity-30" />
        <div className="brand-bloom brand-bloom-blue w-[440px] h-[440px] bottom-0 -right-20 opacity-30" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Enjoy product demos
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Eleven modules —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    one showreel.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A minute inside the platform — registration, dashboards, on-site
                communications, and reporting, threaded end-to-end.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative rounded-3xl overflow-hidden border border-[var(--border)] shadow-[0_30px_80px_-30px_rgba(3,105,161,0.35)] group">
              <video
                data-testid="event-tech-showreel"
                className="w-full aspect-video object-cover"
                src="/event-tech/showreel.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050A14]/85 via-[#050A14]/25 to-[#050A14]/40" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#050A14]/50 via-transparent to-transparent" />
              <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-cyan-400/25 blur-3xl" />

              <span className="pointer-events-none absolute top-5 left-5 font-mono-brand text-[10px] uppercase tracking-[0.28em] text-cyan-300/90 hidden md:block">
                / event-tech.reel · 001
              </span>
              <span className="pointer-events-none absolute top-5 right-5 hidden md:flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.28em] text-white/70">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
                Live loop
              </span>

              <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12 lg:p-16">
                <div className="max-w-2xl">
                  <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/30 backdrop-blur w-fit">
                    <Play size={12} /> See it in motion
                  </span>
                  <h3 className="mt-5 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-white text-balance">
                    Ready to see your program{" "}
                    <span className="hero-accent italic font-medium">run like this?</span>
                  </h3>
                  <p className="mt-4 text-white/75 text-base md:text-lg max-w-xl leading-relaxed">
                    We&rsquo;ll stand up a live demo against your next event brief —
                    modules, integrations, and dashboards mapped to your KPIs.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-4">
                    <Link to="/contact" className="btn-primary" data-testid="reel-cta-quote">
                      Book a Live Demo <ArrowRight size={14} />
                    </Link>
                    <Link
                      to="/services"
                      className="inline-flex items-center gap-2 text-white/90 hover:text-cyan-300 transition-colors font-mono-brand text-[11px] uppercase tracking-[0.22em]"
                      data-testid="reel-cta-services"
                    >
                      Explore all services <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* METRICS BAND */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "98%", v: "Check-in success rate" },
            { k: "<3s", v: "Avg badge print time" },
            { k: "24/7", v: "Show-day operations" },
            { k: "0", v: "Blind spots" },
          ].map((s) => (
            <Reveal key={s.v}>
              <div className="card-white p-6 md:p-8 text-center">
                <div className="font-editorial text-4xl md:text-5xl text-[var(--accent)] tracking-tight">{s.k}</div>
                <div className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">{s.v}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA — removed; global footer CTA covers this */}
    </div>
  );
}
